import { useMemo } from "react";
import { useT } from "../i18n";
import { bottomActive, bottomItems } from "../nav/bottom";
import { useSession } from "../state/session";
import { Icon } from "./Icon";

/**
 * Phone bottom bar (ADR 0030): four role-gated shortcuts and "Menu" (opens the drawer). Shown only below
 * 720 px by CSS; tablets and desktops use the rail. Sits above the home indicator (safe-area inset).
 */
export function BottomBar({
  activeHref,
  alarmCount,
  onOpenMenu,
  menuOpen,
}: {
  activeHref: string;
  alarmCount: number;
  onOpenMenu: () => void;
  menuOpen: boolean;
}) {
  const t = useT();
  const { role, platformAdmin } = useSession();
  const items = useMemo(() => (role ? bottomItems(role, platformAdmin) : []), [role, platformAdmin]);
  if (items.length === 0) return null;
  const active = bottomActive(items, activeHref);
  return (
    <nav className="bottom-bar" aria-label={t("bottomNav.label")}>
      <ul>
        {items.map((it) => {
          const badge = it.id === "alarms" && alarmCount > 0 ? alarmCount : 0;
          return (
            <li key={it.id}>
              <a href={it.href} className={`bottom-item${active === it.id ? " is-active" : ""}`} aria-current={active === it.id ? "page" : undefined}>
                <span className="bottom-icon">
                  <Icon name={it.icon} size={20} />
                  {badge > 0 && (
                    <span className="bottom-badge mono" aria-hidden="true">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>
                <span className="bottom-label">{t(it.labelKey)}</span>
                {badge > 0 && <span className="sr-only">{t("bottomNav.alarmsOpen", { count: badge })}</span>}
              </a>
            </li>
          );
        })}
        <li>
          <button type="button" className="bottom-item" onClick={onOpenMenu} aria-expanded={menuOpen} aria-controls="rail">
            <span className="bottom-icon">
              <Icon name="menu" size={20} />
            </span>
            <span className="bottom-label">{t("bottomNav.menu")}</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
